// ======================================================
// Message-Signaturen — ECDSA-P256
// ======================================================
// Port aus renex-legacy/js/e2e.js (Lines 660-731).
//
// Schützt ctB64 vor Backend-Manipulation:
//   sign(privKey, `${ivB64}|${ctB64}|${sid}|${epoch}`) → sigB64
//   verify(pubKey, sigB64, gleicher Input) → true/false
//
// Sender-PrivKey kommt aus e2eKeys.js (lokal in IDB).
// Empfänger-PubKey kommt aus cmk.js (Peer-Device-Cache).
//
// Spec: docs/MULTI_DEVICE.md §4.2 (Send-Flow, Schritt "Signiere")
// ======================================================

import { bytesToB64, b64ToBytes } from './bytes.js';
import { loadSigningPrivKey } from './e2eKeys.js';

// ── v4 (Double-Ratchet, P3.1) — eigene Signatur-Domäne ──
// Deckt den Ratchet-HEADER ab (nicht nur iv|ct) → Header ist zusätzlich zur
// AES-GCM-AAD auch identitäts-signiert (§4.4). Getrennte Domäne `renex:msgv4:`
// gegen Cross-Protocol-Verwechslung mit dem v2-`iv|ct|sid|epoch`-Format.
function _v4Canon(headerB64, ivB64, ctB64) {
  return new TextEncoder().encode(`renex:msgv4:${headerB64}|${ivB64}|${ctB64}`);
}
export async function signMessageV4(headerB64, ivB64, ctB64) {
  const privKey = await loadSigningPrivKey();
  if (!privKey) throw new Error('No signing key — initE2EKeys + uploadInboxKeyIfNeeded first');
  const sig = await crypto.subtle.sign({ name: 'ECDSA', hash: 'SHA-256' }, privKey, _v4Canon(headerB64, ivB64, ctB64));
  return bytesToB64(new Uint8Array(sig));
}
export async function verifyMessageSigV4(headerB64, ivB64, ctB64, sigB64, pubJwk) {
  try {
    if (!pubJwk || typeof pubJwk !== 'object') return false;
    if (pubJwk.kty !== 'EC' || pubJwk.crv !== 'P-256') return false;
    if (typeof pubJwk.x !== 'string' || typeof pubJwk.y !== 'string') return false;
    if (pubJwk.d !== undefined) return false;
    const pubKey = await crypto.subtle.importKey('jwk', pubJwk, { name: 'ECDSA', namedCurve: 'P-256' }, false, ['verify']);
    return await crypto.subtle.verify({ name: 'ECDSA', hash: 'SHA-256' }, pubKey, b64ToBytes(sigB64), _v4Canon(headerB64, ivB64, ctB64));
  } catch {
    return false;
  }
}

// loadSigningPrivKey wird zentral aus e2eKeys.js bezogen — sie liefert den
// non-extractable CryptoKey (mit Legacy-JWK-Migration on-the-fly).
// Spec-Hardening 2026-05-02 H1.

/**
 * Signiert eine Message mit dem Device-Private-Key.
 * Format: `${ivB64}|${ctB64}|${sid}|${epoch}` — UTF-8 → SHA-256 → ECDSA
 *
 * @returns {Promise<string>} Signature als Base64
 */
export async function signMessage(ivB64, ctB64, sid, epoch) {
  const privKey = await loadSigningPrivKey();
  if (!privKey) throw new Error('No signing key — initE2EKeys + uploadInboxKeyIfNeeded first');

  const data = new TextEncoder().encode(`${ivB64}|${ctB64}|${sid}|${epoch}`);
  const sig = await crypto.subtle.sign(
    { name: 'ECDSA', hash: 'SHA-256' },
    privKey,
    data
  );
  return bytesToB64(new Uint8Array(sig));
}

/**
 * Verifiziert eine Message-Signatur gegen den Sender-PubKey.
 * Returns false bei jedem Fehler (Verify-failed, Key-Import-failed, etc.).
 *
 * @param {string} ivB64
 * @param {string} ctB64
 * @param {string} sid
 * @param {number} epoch
 * @param {string} sigB64
 * @param {object} pubJwk - Sender-Sig-Pubkey (aus Peer-Device-Cache)
 * @returns {Promise<boolean>}
 */
export async function verifyMessageSig(ivB64, ctB64, sid, epoch, sigB64, pubJwk) {
  try {
    // Schema-Check vor importKey: kty=EC, crv=P-256, x+y vorhanden, kein 'd'.
    // Verhindert subtle Bugs falls Peer-Cache stale/malicious Daten enthält.
    if (!pubJwk || typeof pubJwk !== 'object') return false;
    if (pubJwk.kty !== 'EC' || pubJwk.crv !== 'P-256') return false;
    if (typeof pubJwk.x !== 'string' || typeof pubJwk.y !== 'string') return false;
    if (pubJwk.d !== undefined) return false;  // public key only

    const pubKey = await crypto.subtle.importKey(
      'jwk', pubJwk,
      { name: 'ECDSA', namedCurve: 'P-256' },
      false, ['verify']
    );
    const data = new TextEncoder().encode(`${ivB64}|${ctB64}|${sid}|${epoch}`);
    const sig  = b64ToBytes(sigB64);
    return await crypto.subtle.verify(
      { name: 'ECDSA', hash: 'SHA-256' },
      pubKey,
      sig,
      data
    );
  } catch {
    return false;
  }
}
